import { useEffect, useState } from "react";
import { ArrowClockwise } from "@phosphor-icons/react";
import { api } from "../../lib/api";

const STATUS_STYLES = {
  paid: "bg-primary/10 text-primary border-primary/30",
  pending: "bg-accent/10 text-accent border-accent/30",
  failed: "bg-destructive/10 text-destructive border-destructive/30",
  expired: "bg-muted text-muted-foreground border-border",
  refunded: "bg-muted text-muted-foreground border-border",
};

const PLAN_NAMES = {
  clarity_session: "Clarity Session",
  compass_monthly: "Compass Membership",
};

const money = (cents, currency) =>
  (currency || "usd").toUpperCase() === "USD"
    ? "$" + (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : `${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${(currency || "").toUpperCase()}`;

export default function Payments() {
  const [payments, setPayments] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => setError("Could not load payments. Please try again."));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="admin-payments">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-primary tracking-tight">Payments</h1>
          <p className="mt-2 text-sm text-foreground/60" data-testid="admin-payments-count">
            {payments ? `${payments.length} transaction${payments.length === 1 ? "" : "s"}` : "Loading…"}
          </p>
        </div>
        <button
          data-testid="admin-payments-refresh"
          onClick={load}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
        >
          <ArrowClockwise size={15} weight="bold" /> Refresh
        </button>
      </div>
      {error && <p className="mt-4 text-sm font-semibold text-destructive" data-testid="admin-payments-error">{error}</p>}

      <div className="mt-10 bg-card border border-border rounded-sm overflow-x-auto" data-testid="admin-payments-table-wrap">
        <table className="w-full text-sm min-w-[760px]" data-testid="admin-payments-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Date", "Plan", "Amount", "Customer", "Status"].map((h) => (
                <th key={h} className="px-5 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments && payments.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-14 text-center text-foreground/50 font-medium" data-testid="admin-payments-empty">
                  No payments yet — they'll appear here the moment someone checks out.
                </td>
              </tr>
            )}
            {payments &&
              payments.map((p) => (
                <tr key={p.session_id} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors duration-200" data-testid={`admin-payment-row-${p.session_id}`}>
                  <td className="px-5 py-4 whitespace-nowrap text-foreground/60">
                    {new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </td>
                  <td className="px-5 py-4 font-semibold text-foreground whitespace-nowrap">
                    {PLAN_NAMES[p.lookup_key] || p.lookup_key}
                    {p.provider === "paypal" && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#003087] bg-[#FFC439]/40 border border-[#FFC439] rounded-full px-2 py-0.5">PayPal</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-foreground/70">{money(p.amount, p.currency)}</td>
                  <td className="px-5 py-4 text-foreground/70">{p.customer_email || "—"}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-bold uppercase tracking-wider border rounded-full px-3 py-1.5 ${STATUS_STYLES[p.payment_status] || STATUS_STYLES.pending}`} data-testid={`admin-payment-status-${p.session_id}`}>
                      {p.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
